// Print Receipt Dialog Component
import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { 
  Printer, Bluetooth, BluetoothConnected, BluetoothOff, 
  Check, X, Loader2, AlertCircle, RefreshCw 
} from 'lucide-react';
import { toast } from 'sonner';
import { bluetoothPrinter } from '@/services/BluetoothPrinterService';

export function PrintReceiptDialog({ 
  isOpen, 
  onClose, 
  saleData,
  businessName,
  branchName,
}) {
  const [isSupported, setIsSupported] = useState(true);
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [printerName, setPrinterName] = useState('');
  const [printSuccess, setPrintSuccess] = useState(false);

  useEffect(() => {
    // Check if Web Bluetooth is supported
    setIsSupported(bluetoothPrinter.isSupported());
    setIsConnected(bluetoothPrinter.isConnected);
  }, [isOpen]);

  const handleConnect = async () => {
    setIsConnecting(true);
    try {
      const device = await bluetoothPrinter.connect();
      setIsConnected(true);
      setPrinterName(device.name);
      toast.success(`Conectado a ${device.name}`);
    } catch (error) {
      toast.error(error.message);
      setIsConnected(false);
    } finally {
      setIsConnecting(false);
    }
  };

  const handleDisconnect = () => {
    bluetoothPrinter.disconnect();
    setIsConnected(false);
    setPrinterName('');
    toast.info('Impresora desconectada');
  };

  const handlePrint = async () => {
    if (!isConnected) {
      toast.error('Conecta una impresora primero');
      return;
    }

    setIsPrinting(true);
    setPrintSuccess(false);
    
    try {
      const receiptData = {
        businessName: businessName || 'THE HOLLAND BARBERSHOP',
        branchName: branchName,
        ticketNumber: saleData?.id?.substring(0, 8).toUpperCase(),
        barberName: saleData?.barberName,
        items: saleData?.items || [],
        total: saleData?.total || 0,
        paymentMethod: saleData?.paymentMethod || 'cash',
        cashAmount: saleData?.cashAmount,
        cardAmount: saleData?.cardAmount,
        loyaltyPoints: saleData?.loyaltyPoints,
        footer: '¡Gracias por su preferencia!',
      };

      await bluetoothPrinter.printReceipt(receiptData);
      setPrintSuccess(true);
      toast.success('Ticket impreso correctamente');
    } catch (error) {
      toast.error(`Error al imprimir: ${error.message}`);
    } finally {
      setIsPrinting(false);
    }
  };

  const handleClose = () => {
    setPrintSuccess(false);
    onClose();
  };

  if (!isSupported) {
    return (
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-white flex items-center gap-2">
              <Printer className="w-5 h-5" />
              Imprimir Ticket
            </DialogTitle>
          </DialogHeader>
          
          <div className="p-6 text-center">
            <AlertCircle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-white mb-2">
              Navegador no compatible
            </h3>
            <p className="text-zinc-400 text-sm">
              Web Bluetooth no está soportado en este navegador. 
              Por favor usa <span className="text-gold">Google Chrome</span> o{' '}
              <span className="text-gold">Microsoft Edge</span> en tu computadora o Android.
            </p>
            <Button
              onClick={handleClose}
              className="mt-6 bg-zinc-800 hover:bg-zinc-700"
            >
              Entendido
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md max-h-[90vh] p-0 overflow-hidden">
        <DialogHeader className="p-4 border-b border-zinc-800">
          <DialogTitle className="font-heading text-xl text-white flex items-center gap-2">
            <Printer className="w-5 h-5" />
            Imprimir Ticket
          </DialogTitle>
        </DialogHeader>
        
        <div className="p-4 space-y-4 overflow-y-auto max-h-[calc(90vh-80px)]">
          {/* Connection Status */}
          <div className={`p-4 rounded-lg border-2 ${
            isConnected 
              ? 'bg-green-500/10 border-green-500/30' 
              : 'bg-zinc-800/50 border-zinc-700'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {isConnected ? (
                  <BluetoothConnected className="w-8 h-8 text-green-400" />
                ) : (
                  <BluetoothOff className="w-8 h-8 text-zinc-500" />
                )}
                <div>
                  <p className={`font-medium ${isConnected ? 'text-green-400' : 'text-zinc-400'}`}>
                    {isConnected ? 'Conectado' : 'Sin conexión'}
                  </p>
                  <p className="text-sm text-zinc-500">
                    {isConnected ? printerName : 'Ninguna impresora conectada'}
                  </p>
                </div>
              </div>
              
              {isConnected ? (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleDisconnect}
                  className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                >
                  <X className="w-4 h-4" />
                </Button>
              ) : (
                <Button
                  onClick={handleConnect}
                  disabled={isConnecting}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="connect-printer-btn"
                >
                  {isConnecting ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Bluetooth className="w-4 h-4 mr-2" />
                  )}
                  {isConnecting ? 'Buscando...' : 'Conectar'}
                </Button>
              )}
            </div>
          </div>

          {/* Receipt Preview - 48mm width simulation */}
          <div className="bg-white text-black p-3 rounded-lg font-mono text-[11px] leading-tight space-y-0.5 max-h-64 overflow-y-auto" style={{ maxWidth: '280px', margin: '0 auto' }}>
            <div className="text-center font-bold text-sm">
              {(businessName || 'THE HOLLAND BARBERSHOP').toUpperCase().split(' ').length > 2 ? (
                <>
                  <div>{(businessName || 'THE HOLLAND').split(' ').slice(0, 2).join(' ')}</div>
                  <div>{(businessName || 'BARBERSHOP').split(' ').slice(2).join(' ')}</div>
                </>
              ) : (
                businessName || 'THE HOLLAND BARBERSHOP'
              )}
            </div>
            {branchName && (
              <div className="text-center text-gray-600">{branchName}</div>
            )}
            <div className="border-t border-dashed border-gray-400 my-1" />
            
            <div className="text-gray-600">
              Fecha: {new Date().toLocaleDateString('es-MX')} {new Date().toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}
            </div>
            {saleData?.id && (
              <div>Ticket: #{saleData.id.substring(0, 8).toUpperCase()}</div>
            )}
            {saleData?.barberName && (
              <div>Atendio: {saleData.barberName}</div>
            )}
            
            <div className="border-t border-dashed border-gray-400 my-1" />
            
            <div className="flex justify-between font-bold">
              <span>DESCRIPCION</span>
              <span>IMPORTE</span>
            </div>
            {saleData?.items?.map((item, idx) => (
              <div key={idx} className="flex justify-between">
                <span className="truncate mr-2">{item.quantity}x {item.name}</span>
                <span className="whitespace-nowrap">${(item.price * item.quantity).toFixed(2)}</span>
              </div>
            ))}
            
            <div className="border-t border-dashed border-gray-400 my-1" />
            
            <div className="flex justify-between font-bold text-base">
              <span>TOTAL:</span>
              <span>${saleData?.total?.toFixed(2) || '0.00'}</span>
            </div>
            
            <div className="text-gray-600">
              Pago: {
                saleData?.paymentMethod === 'cash' ? 'Efectivo' :
                saleData?.paymentMethod === 'card' ? 'Tarjeta' :
                saleData?.paymentMethod === 'transfer' ? 'Transferencia' :
                saleData?.paymentMethod === 'combined' ? 'Combinado' : 
                saleData?.paymentMethod
              }
            </div>
            
            {saleData?.paymentMethod === 'combined' && (
              <>
                <div className="text-gray-600 pl-2">
                  Efectivo: ${saleData?.cashAmount?.toFixed(2)}
                </div>
                <div className="text-gray-600 pl-2">
                  Tarjeta: ${saleData?.cardAmount?.toFixed(2)}
                </div>
              </>
            )}
            
            <div className="border-t border-dashed border-gray-400 my-1" />
            <div className="text-center text-gray-600">
              Gracias por su preferencia!
            </div>
            {saleData?.loyaltyPoints && (
              <div className="text-center text-gray-600">
                +{saleData.loyaltyPoints} puntos de lealtad
              </div>
            )}
          </div>

          {/* Print Success Message */}
          {printSuccess && (
            <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 flex items-center gap-2">
              <Check className="w-5 h-5 text-green-400" />
              <span className="text-green-400">Ticket impreso correctamente</span>
            </div>
          )}

          {/* Actions */}
          <div className="grid grid-cols-2 gap-3 pt-2">
            <Button
              variant="outline"
              onClick={handleClose}
              className="h-12 border-zinc-700 text-zinc-300 hover:bg-zinc-800"
            >
              {printSuccess ? 'Cerrar' : 'Cancelar'}
            </Button>
            <Button
              onClick={handlePrint}
              disabled={!isConnected || isPrinting}
              className="h-12 bg-gold text-black hover:bg-gold-dark disabled:opacity-50"
              data-testid="print-receipt-btn"
            >
              {isPrinting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Imprimiendo...
                </>
              ) : (
                <>
                  <Printer className="w-4 h-4 mr-2" />
                  Imprimir
                </>
              )}
            </Button>
          </div>

          {/* Retry button if already printed */}
          {printSuccess && (
            <Button
              variant="ghost"
              onClick={handlePrint}
              disabled={isPrinting}
              className="w-full text-zinc-400 hover:text-white"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Imprimir otra copia
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
